import { ATTR_NAME } from 'constants/attr-name'
import { CLASS_NAME } from 'constants/class-names'
import { EventType } from 'constants/enums'
import { Nullable } from 'constants/types'
import { ApiService } from 'services/api/api.service'
import { ComponentFactory } from 'services/component-factory'
import { ComponentBase } from 'utils/abstract-classes/component-base'

const {
    driving: { findCompare: attrName }
} = ATTR_NAME
const { findCompare: className } = CLASS_NAME
const { ad: adClassName } = CLASS_NAME

/**
 * Find-Compare page component
 * This manages the following features
 *
 * 1. Load more
 * 2. Handle event listeners to change cards status
 */
export class FindCompare extends ComponentBase {
    private apiService: ApiService
    private curPage = 1
    // First dynamically added ad is slot #3
    // eslint-disable-next-line no-magic-numbers
    private adCounter = 3
    // This should match with content_services/driving/views/app_views.py
    // eslint-disable-next-line no-magic-numbers
    private readonly DEFAULT_PAGE_SIZE = 12

    // API parameters
    private make: string
    private bodystyle: string
    private year: string
    private use_latest_year: string
    private price_min: string
    private price_max: string
    private seats: string
    private power_source: string
    private transmission: string
    private sort: string

    // HTML Elements
    private listBottom: HTMLDivElement
    private loadMore: HTMLDivElement
    private adTemplate: HTMLElement

    /**
     * Get the load more button
     *
     * @returns {Nullable<HTMLButtonElement>}
     */
    private get loadMoreButton(): Nullable<HTMLButtonElement> {
        return this.getElement(`.${className.loadMoreButton}`)
    }

    public constructor(wrapper: HTMLElement) {
        super()

        const listBottom = this.getElement<HTMLDivElement>(
            `.${className.listBottom}`,
            wrapper
        )
        const loadMore = this.getElement<HTMLDivElement>(
            `.${className.loadMore}`,
            wrapper
        )
        const adDiv = this.getElement<HTMLElement>(
            `.${className.adSlot}`,
            wrapper
        )

        if (!listBottom || !loadMore || !adDiv) {
            return
        }

        this.listBottom = listBottom
        this.loadMore = loadMore

        this.adTemplate = this.makeAdTemplate(adDiv)

        this.make = wrapper.getAttribute(attrName.makes) || ''
        this.bodystyle = wrapper.getAttribute(attrName.bodystyles) || ''
        this.year = wrapper.getAttribute(attrName.years) || ''
        // The use_latest_year will set True with '' in ModelViewSet from api_views.py
        this.use_latest_year =
            wrapper.getAttribute(attrName.use_latest_year) || 'false'
        this.price_min = wrapper.getAttribute(attrName.price_min) || ''
        this.price_max = wrapper.getAttribute(attrName.price_max) || ''
        this.seats = wrapper.getAttribute(attrName.seats) || ''
        this.power_source = wrapper.getAttribute(attrName.power_source) || ''
        this.transmission = wrapper.getAttribute(attrName.transmission) || ''
        this.sort = wrapper.getAttribute(attrName.sort) || ''

        if (this.loadMoreButton) {
            this.loadMoreButton.addEventListener(EventType.CLICK, () => {
                this.loadMoreAction()
            })
        }

        this.apiService = new ApiService()
    }

    /**
     * Load More button click behaviour
     */
    private loadMoreAction(): void {
        this.removeButton()
        this.renderLoading()

        this.curPage += 1
        this.apiService
            .fetchDrivingModels<Response>({
                make: this.make,
                bodystyle: this.bodystyle,
                year: this.year,
                price_min: this.price_min,
                price_max: this.price_max,
                use_latest_year: this.use_latest_year,
                seats: this.seats,
                power_source: this.power_source,
                transmission: this.transmission,
                page: this.curPage,
                page_size: this.DEFAULT_PAGE_SIZE,
                ordering: this.sort,
                format: 'html'
            })
            .then(async (data: Response) => {
                const parser = new DOMParser()
                const text = await data.text()
                const doc = parser.parseFromString(text, 'text/html')
                const vehicleListHolderTemplate = document.createElement('div')
                vehicleListHolderTemplate.className = 'row vehicle-list'
                const parentWrapper = <HTMLElement>this.listBottom.parentNode
                const cardsPerBox = 6
                let nextListBox: HTMLElement

                // Extract all of the model cards
                const articleElements = this.getElements(
                    `.${className.card}`,
                    doc
                )
                let firstCard: Nullable<HTMLAnchorElement>

                // insert first set of 6 cards
                nextListBox = <HTMLElement>vehicleListHolderTemplate.cloneNode()
                articleElements.slice(0, cardsPerBox).forEach((element) => {
                    if (element.parentElement) {
                        nextListBox.append(element.parentElement)

                        firstCard =
                            firstCard ||
                            this.getElement<HTMLAnchorElement>(
                                `.${className.cardContent}`,
                                element
                            )
                    }
                })
                parentWrapper.insertBefore(nextListBox, this.listBottom)

                const [adDiv, adDivInnerContainer] = this.generateAdDiv(
                    this.adCounter
                )
                parentWrapper.insertBefore(adDiv, this.listBottom)

                // insert last set of cards (if any)
                if (articleElements.length > cardsPerBox) {
                    nextListBox = <HTMLElement>(
                        vehicleListHolderTemplate.cloneNode()
                    )
                    articleElements.slice(cardsPerBox).forEach((element) => {
                        if (element.parentElement) {
                            nextListBox.append(element.parentElement)
                        }
                    })
                    parentWrapper.insertBefore(nextListBox, this.listBottom)
                }

                // call FEM to fill the ad slot via event listener
                dispatchEvent(
                    new CustomEvent('adSlotAdded', {
                        detail: { el: [adDivInnerContainer] }
                    })
                )
                this.adCounter++

                if (firstCard) {
                    firstCard.focus()
                }

                this.removeLoading()
                if (data.headers.get('next')) {
                    this.renderButton()
                }

                // Bind model card component
                new ComponentFactory('driving', 'modelCard')
            })
            .catch((err) => {
                console.error(err)
                this.removeLoading()
                this.renderButton()
            })
    }

    /**
     * Show button
     */
    private renderButton(): void {
        if (!this.loadMoreButton) {
            return
        }
        this.loadMoreButton.classList.remove(CLASS_NAME.common.hidden)
    }

    /**
     * Hide button
     */
    private removeButton(): void {
        if (!this.loadMoreButton) {
            return
        }
        this.loadMoreButton.classList.add(CLASS_NAME.common.hidden)
    }

    /**
     * Show loading
     */
    private renderLoading(): void {
        const div = document.createElement('div')
        div.classList.add(className.loading)
        div.innerText = 'loading...'

        this.removeLoading()
        this.loadMore.append(div)
    }

    /**
     * Hide loading
     */
    private removeLoading(): void {
        this.getElements(`.${className.loading}`).forEach((element) =>
            element.remove()
        )
    }

    /**
     * Create a template to be used for adding additional ads into the results
     * @param {HTMLElement} adDiv
     * @returns {HTMLElement}
     */
    private makeAdTemplate(adDiv: HTMLElement) {
        const adTemplate = <HTMLElement>adDiv.cloneNode(true)
        const adTemplateInner = <HTMLElement>(
            adTemplate.querySelector(`.${adClassName.innerContainer}`)
        )
        adTemplateInner.innerHTML = `<div class="${adClassName.placeholder}">
            <div class="${adClassName.placeholderInner}">
                This advertisement has not loaded yet, but your article continues below.
            </div>
        </div>`
        adTemplateInner.removeAttribute('data-google-query-id')
        adTemplateInner.removeAttribute('data-ad-width')
        return adTemplate
    }

    /**
     * Generate a clone of this.adTemplate with values set properly and return
     * elements needed for inserting and sending event to FEM for filling the
     * slot
     * @param {number} adCounter
     * @returns {HTMLElement[]} [adDiv, adDivInnerContainer]
     */
    private generateAdDiv(adCounter: number) {
        const adDiv = this.adTemplate.cloneNode(true) as HTMLElement
        const adDivInner = <HTMLElement>(
            adDiv.querySelector(`.${adClassName.inner}`)
        )
        adDivInner.id = `ad__inner-${adCounter}`
        const adDivInnerContainer = <HTMLElement>(
            adDivInner.querySelector(`.${adClassName.innerContainer}`)
        )
        adDivInnerContainer.id = `ad-${adCounter}`
        adDivInnerContainer.setAttribute('data-ad-loc', `${adCounter}`)
        return [adDiv, adDivInnerContainer]
    }
}
